import { IApi } from '@/setup/api-client'
import { toast } from '@/features/toast/toast'
import { toastBoardMutationError } from '@/features/ad-boards/utils/board-error-toast'
import { useApiMutation } from '@/utils/api/api-mutation'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import type {
    CreateAdBoardBody,
    CreateAdBoardData,
} from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/boards'
import type {
    UpdateAdBoardBody,
    UpdateAdBoardData,
} from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/boards/[board_uuid]'
import type { RestoreAdBoardData } from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/boards/[board_uuid]/restore'
import type {
    AddAdBoardItemBody,
    RemoveAdBoardItemBody,
} from 'api/dist/routes/organization/[organization_id]/brands/[brand_slug]/boards/[board_uuid]/items'

type IBoardScope = { organizationId: string; brandSlug: string }

const boardsUrl = (scope: IBoardScope) => `/organization/${scope.organizationId}/brands/${scope.brandSlug}/boards`

class AdBoardApi {
    static create(api: IApi, scope: IBoardScope, body: CreateAdBoardBody) {
        return api
            .post<{ data: CreateAdBoardData }, CreateAdBoardBody>(boardsUrl(scope), body)
            .then((res) => (res as unknown as { data: CreateAdBoardData }).data)
    }

    static update(api: IApi, scope: IBoardScope, boardUuid: string, body: UpdateAdBoardBody) {
        return api
            .patch<{ data: UpdateAdBoardData }, UpdateAdBoardBody>(`${boardsUrl(scope)}/${boardUuid}`, body)
            .then((res) => (res as unknown as { data: UpdateAdBoardData }).data)
    }

    static archive(api: IApi, scope: IBoardScope, boardUuid: string) {
        return api.delete(`${boardsUrl(scope)}/${boardUuid}`)
    }

    static restore(api: IApi, scope: IBoardScope, boardUuid: string) {
        return api
            .post<{ data: RestoreAdBoardData }, Record<string, never>>(`${boardsUrl(scope)}/${boardUuid}/restore`, {})
            .then((res) => (res as unknown as { data: RestoreAdBoardData }).data)
    }

    static destroy(api: IApi, scope: IBoardScope, boardUuid: string) {
        return api.post(`${boardsUrl(scope)}/${boardUuid}/destroy`, {})
    }

    static addItem(api: IApi, scope: IBoardScope, boardUuid: string, body: AddAdBoardItemBody) {
        return api.post<unknown, AddAdBoardItemBody>(`${boardsUrl(scope)}/${boardUuid}/items`, body)
    }

    static removeItem(api: IApi, scope: IBoardScope, boardUuid: string, body: RemoveAdBoardItemBody) {
        return api.delete<unknown, RemoveAdBoardItemBody>(`${boardsUrl(scope)}/${boardUuid}/items`, body)
    }

    static reorderItems(api: IApi, scope: IBoardScope, boardUuid: string, body: { itemUuids: string[] }) {
        return api.patch<unknown, { itemUuids: string[] }>(`${boardsUrl(scope)}/${boardUuid}/items`, body)
    }

    static updateItemNote(api: IApi, scope: IBoardScope, boardUuid: string, body: { adUuid: string; note: string | null }) {
        return api.patch<unknown, { adUuid: string; note: string | null }>(`${boardsUrl(scope)}/${boardUuid}/items`, body)
    }
}

/** Every board mutation invalidates the whole `ad-boards` tree — lists carry item counts. */
const useInvalidateBoards = () => {
    const { qc } = useApiToolbox()
    return () => qc.invalidateQueries({ queryKey: ['ad-boards'] })
}

type ICachedBoardShape = {
    uuid: string
    itemCount?: number | null
    canPublish?: boolean | null
    containsAd?: boolean | null
    /** The overview card's preview mosaic — the write/restore transformer returns it empty. */
    previewFiles?: unknown[]
}

/**
 * The write routes respond with the board alone — context fields the transformer cannot know
 * there (`itemCount`, `canPublish`, `containsAd`, `previewFiles`) come back null/empty. The cached
 * entry knows them; overwriting a real count with null, or the preview mosaic with [], would flash
 * the card to "0 ads" with an empty preview until the refetch converges.
 */
const mergeFreshBoard = (fresh: ICachedBoardShape, cached: ICachedBoardShape) => ({
    ...fresh,
    itemCount: fresh.itemCount ?? cached.itemCount ?? null,
    canPublish: fresh.canPublish ?? cached.canPublish ?? null,
    containsAd: fresh.containsAd ?? cached.containsAd ?? null,
    previewFiles: fresh.previewFiles?.length ? fresh.previewFiles : cached.previewFiles ?? [],
})

/**
 * Writes a fresh write-response board into every cached structure that holds it (list arrays and
 * the detail's `board` field). The invalidation that follows refetches from a replica that may
 * still lag the write — this keeps the UI on the authoritative response until the refetch
 * converges.
 */
const writeBoardIntoCaches = (
    qc: ReturnType<typeof useApiToolbox>['qc'],
    board: ICachedBoardShape | null | undefined
) => {
    if (!board) return

    qc.setQueriesData({ queryKey: ['ad-boards'] }, (old: unknown) => {
        if (Array.isArray(old)) {
            return old.map((entry) =>
                (entry as ICachedBoardShape | undefined)?.uuid === board.uuid
                    ? mergeFreshBoard(board, entry as ICachedBoardShape)
                    : entry
            )
        }
        if (
            old !== null &&
            typeof old === 'object' &&
            'board' in old &&
            (old as { board?: ICachedBoardShape }).board?.uuid === board.uuid
        ) {
            return { ...old, board: mergeFreshBoard(board, (old as { board: ICachedBoardShape }).board) }
        }
        return old
    })
}

/**
 * Restore moves a board across lists (archived → active). The active list is almost always inactive
 * when restore runs — the user is on the Archived tab — so its invalidation only marks it stale, and
 * the refetch that eventually runs races the write's replica lag, leaving the board absent from
 * Active until a hard reload. Prime the caches from the authoritative restore response instead: drop
 * it from every archived list and prepend it to every active list. The invalidation that follows
 * reconverges ordering and the context fields (itemCount, canPublish) a write response can't know.
 *
 * The restore response itself is stripped of those context fields (itemCount/canPublish come back
 * null, the preview mosaic empty), so prepending it raw would flash the just-restored card as
 * "0 ads" with an empty preview until the refetch lands. The correct metadata is right there in the
 * archived cache entry we're dropping — carry it forward via `mergeFreshBoard`.
 */
const moveRestoredBoardIntoActiveCaches = (
    qc: ReturnType<typeof useApiToolbox>['qc'],
    board: ICachedBoardShape
) => {
    const queries = qc.getQueryCache().findAll({ queryKey: ['ad-boards'] })

    let archivedEntry: ICachedBoardShape | undefined
    for (const query of queries) {
        if (query.queryKey[2] !== 'archived' || !Array.isArray(query.state.data)) continue
        const found = (query.state.data as ICachedBoardShape[]).find((entry) => entry?.uuid === board.uuid)
        if (found) {
            archivedEntry = found
            break
        }
    }
    const restoredEntry = archivedEntry ? mergeFreshBoard(board, archivedEntry) : board

    for (const query of queries) {
        // GetAdBoardsQuery.getKey: ['ad-boards', brandSlug, 'active' | 'archived', forAdUuid]. Detail
        // caches hold an object rather than a list, so the array guard skips them.
        const listKind = query.queryKey[2]
        if (!Array.isArray(query.state.data)) continue

        if (listKind === 'archived') {
            qc.setQueryData(query.queryKey, (old: unknown) =>
                Array.isArray(old) ? old.filter((entry) => (entry as ICachedBoardShape)?.uuid !== board.uuid) : old
            )
        } else if (listKind === 'active') {
            qc.setQueryData(query.queryKey, (old: unknown) => {
                if (!Array.isArray(old)) return old
                if (old.some((entry) => (entry as ICachedBoardShape)?.uuid === board.uuid)) return old
                return [restoredEntry, ...old]
            })
        }
    }
}

export function useCreateAdBoardMutation() {
    const { api, apiParams, qc } = useApiToolbox()
    const invalidate = useInvalidateBoards()

    return useApiMutation({
        mutationFn: (body: CreateAdBoardBody) => AdBoardApi.create(api, apiParams, body),
        onSuccess: (board) => {
            writeBoardIntoCaches(qc, board)
            invalidate()
        },
        onError: (error) => toastBoardMutationError(error),
    })
}

export function useUpdateAdBoardMutation() {
    const { api, apiParams, qc } = useApiToolbox()
    const invalidate = useInvalidateBoards()

    return useApiMutation({
        mutationFn: ({ boardUuid, ...body }: UpdateAdBoardBody & { boardUuid: string }) =>
            AdBoardApi.update(api, apiParams, boardUuid, body),
        onSuccess: (board) => {
            writeBoardIntoCaches(qc, board)
            invalidate()
        },
        onError: (error) => toastBoardMutationError(error),
    })
}

// Archive/restore toasts live on the mutation lifecycle, not at call sites: a call-site
// success toast after a swallowed rejection reports "archived" for a board that is still
// there (PR #3240 review finding).
export function useArchiveAdBoardMutation() {
    const { api, apiParams } = useApiToolbox()
    const invalidate = useInvalidateBoards()

    return useApiMutation({
        mutationFn: (boardUuid: string) => AdBoardApi.archive(api, apiParams, boardUuid),
        onSuccess: () => {
            invalidate()
            toast.success('Board archived')
        },
        onError: (error) => toastBoardMutationError(error),
    })
}

export function useRestoreAdBoardMutation() {
    const { api, apiParams, qc } = useApiToolbox()
    const invalidate = useInvalidateBoards()

    return useApiMutation({
        mutationFn: (boardUuid: string) => AdBoardApi.restore(api, apiParams, boardUuid),
        onSuccess: (board) => {
            if (board) moveRestoredBoardIntoActiveCaches(qc, board)
            invalidate()
            toast.success('Board restored')
        },
        onError: (error) => toastBoardMutationError(error),
    })
}

export function useDestroyAdBoardMutation() {
    const { api, apiParams } = useApiToolbox()
    const invalidate = useInvalidateBoards()

    return useApiMutation({
        mutationFn: (boardUuid: string) => AdBoardApi.destroy(api, apiParams, boardUuid),
        onSuccess: invalidate,
        onError: (error) => toastBoardMutationError(error),
    })
}

// Item writes fire the invalidation without returning its promise: a returned promise makes
// `mutateAsync` (and any spinner awaiting it) block on the full boards-list refetch, doubling the
// perceived latency of the card's save action for a refresh the UI doesn't need to wait for.
export function useAddAdToBoardMutation() {
    const { api, apiParams } = useApiToolbox()
    const invalidate = useInvalidateBoards()

    return useApiMutation({
        mutationFn: ({ boardUuid, ...body }: AddAdBoardItemBody & { boardUuid: string }) =>
            AdBoardApi.addItem(api, apiParams, boardUuid, body),
        onSuccess: () => {
            void invalidate()
        },
    })
}

export function useRemoveAdFromBoardMutation() {
    const { api, apiParams } = useApiToolbox()
    const invalidate = useInvalidateBoards()

    return useApiMutation({
        mutationFn: ({ boardUuid, ...body }: RemoveAdBoardItemBody & { boardUuid: string }) =>
            AdBoardApi.removeItem(api, apiParams, boardUuid, body),
        onSuccess: () => {
            void invalidate()
        },
    })
}

export function useReorderAdBoardItemsMutation() {
    const { api, apiParams } = useApiToolbox()
    const invalidate = useInvalidateBoards()

    return useApiMutation({
        mutationFn: ({ boardUuid, ...body }: { itemUuids: string[]; boardUuid: string }) =>
            AdBoardApi.reorderItems(api, apiParams, boardUuid, body),
        onSuccess: invalidate,
    })
}

export function useUpdateAdBoardItemNoteMutation() {
    const { api, apiParams } = useApiToolbox()
    const invalidate = useInvalidateBoards()

    return useApiMutation({
        mutationFn: ({ boardUuid, ...body }: { adUuid: string; note: string | null; boardUuid: string }) =>
            AdBoardApi.updateItemNote(api, apiParams, boardUuid, body),
        onSuccess: invalidate,
    })
}
